"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, FileText, Mail, Phone, ShieldCheck, Sparkles, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const guarantees = [{
  id: 1,
  title: "100% Direct Consultant Access",
  desc: "You work directly with me from day one, with focused personal guidance and dedicated execution throughout."
}, {
  id: 2,
  title: "Bespoke Strategic Roadmap",
  desc: "Tailored national, international, or technical SEO built for measurable business revenue."
}, {
  id: 3,
  title: "Transparent & Honest Reporting",
  desc: "Clear deliverables, realistic milestones, and verifiable organic search data."
}];
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const motionInitial = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 24
  };
  const motionAnimate = {
    opacity: 1,
    y: 0
  };
  const motionTransition = {
    duration: 0.5,
    ease: "easeOut"
  };
  return <section id="consultationcalltoaction" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      {/* Background ambient accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-secondary/60 blur-3xl -z-10 rounded-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-20 right-10 w-80 h-80 bg-muted blur-2xl -z-10 rounded-2xl" />

      <div className="container mx-auto max-w-6xl px-4 sm:px-6">
        <Card className="relative overflow-hidden border border-border bg-card shadow-xl rounded-2xl">
          {/* Subtle top accent bar */}
          <div className="h-1.5 w-full bg-primary" />

          <CardContent className="p-8 sm:p-12 md:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              
              {/* Left Column: CTA Pitch */}
              <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
              once: true,
              amount: 0.2
            }} transition={motionTransition} className="lg:col-span-7 flex flex-col items-start text-left">
                <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-secondary-foreground border border-border">
                  <Sparkles className="size-3.5 text-accent mr-1" />
                  Tailored SEO Partnership
                </Badge>

                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default leading-tight">
                  Ready to Grow Your Sustainable Search Visibility?
                </h1>

                <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
                  Book a free discovery consultation with me. I will review your current search footprint, pinpoint technical barriers, and outline a realistic, high-impact growth strategy tailored to your exact market.
                </p>

                {/* Key Pillars */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <UserCheck className="size-4 text-primary shrink-0" />
                    <span>Dedicated Strategic Focus</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <ShieldCheck className="size-4 text-primary shrink-0" />
                    <span>Verified Track Record</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Actionable Growth Strategy</span>
                  </div>
                </div>

                <Separator className="my-8" />

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
                  <Button asChild size="lg" className="h-12 px-7 text-base font-medium shadow-md bg-primary text-primary-foreground hover:bg-primary/90 transition-[background-color,box-shadow]">
                    <Link to="/ContactPage">
                      <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                      Book a Free SEO Consultation
                      <ArrowRight className="size-4 ml-2" />
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="h-12 px-6 text-base font-medium border-border hover:bg-muted text-foreground transition-[background-color,border-color]">
                    <Link to="/CaseStudiesPage">
                      <FileText className="size-4 mr-2" />
                      Explore My Case Studies
                    </Link>
                  </Button>
                </div>
              </motion.div>

              {/* Right Column: Direct Contact & Trust Card */}
              <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
              once: true,
              amount: 0.2
            }} transition={{
              ...motionTransition,
              delay: 0.15
            }} className="lg:col-span-5">
                <div className="rounded-xl border border-border bg-background p-6 sm:p-8 shadow-sm">
                  <h2 className="text-xl font-semibold text-foreground font-default">
                    Why Consult Directly With Me?
                  </h2>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Clear accountability and hands-on execution for sustainable search growth.
                  </p>

                  <div className="mt-6 space-y-4">
                    {guarantees.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-3">
                        <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-foreground">{item.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>)}
                  </div>

                  <Separator className="my-6" />

                  <div className="space-y-3 pt-1">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Prefer direct correspondence?
                    </p>
                    <div className="flex flex-col gap-2.5">
                      <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors font-medium">
                        <Mail className="size-4 text-primary shrink-0" />
                        <span>contact@ye-consultancy.com</span>
                      </a>
                      <a href="tel:+447490984430" className="inline-flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors font-medium">
                        <Phone className="size-4 text-primary shrink-0" />
                        <span>+44 7490984430</span>
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>

            </div>
          </CardContent>
        </Card>
      </div>
    </section>;
}